import React, { useEffect, useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { VolumeIcon, PlayIcon, PauseIcon, MicIcon, RepeatIcon, BookmarkIcon, CheckCircleIcon, AlertCircleIcon, InfoIcon, ArrowRightIcon, CheckIcon, BookIcon } from 'lucide-react';
import { Sitemap } from '../components/Sitemap';
import { useToast } from '../components/ui/ToastContainer';
import { Tooltip } from '../components/ui/Tooltip';
import { Link } from 'react-router-dom';
const PRONUNCIATION_LESSONS = [{
  id: 1,
  category: {
    en: 'Vowel Sounds',
    ar: 'أصوات الحروف المتحركة'
  },
  sounds: [{
    symbol: 'æ',
    examples: [{
      word: 'cat',
      phonetic: '/kæt/',
      ar: 'قط'
    }, {
      word: 'hat',
      phonetic: '/hæt/',
      ar: 'قبعة'
    }]
  }, {
    symbol: 'ə',
    examples: [{
      word: 'about',
      phonetic: '/əˈbaʊt/',
      ar: 'حول'
    }, {
      word: 'again',
      phonetic: '/əˈɡen/',
      ar: 'مرة أخرى'
    }]
  }]
}, {
  id: 2,
  category: {
    en: 'Consonant Sounds',
    ar: 'أصوات الحروف الساكنة'
  },
  sounds: [{
    symbol: 'θ',
    examples: [{
      word: 'think',
      phonetic: '/θɪŋk/',
      ar: 'يفكر'
    }, {
      word: 'mouth',
      phonetic: '/maʊθ/',
      ar: 'فم'
    }]
  }, {
    symbol: 'ʃ',
    examples: [{
      word: 'ship',
      phonetic: '/ʃɪp/',
      ar: 'سفينة'
    }, {
      word: 'fish',
      phonetic: '/fɪʃ/',
      ar: 'سمك'
    }]
  }]
}];
export function PronunciationGuide() {
  const {
    isArabic
  } = useLanguage();
  const [selectedSound, setSelectedSound] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [practiceMode, setPracticeMode] = useState(false);
  const [completedSounds, setCompletedSounds] = useState<Set<string>>(new Set());
  const [bookmarkedSounds, setBookmarkedSounds] = useState<Set<string>>(new Set());
  const toast = useToast();
  const playSound = (word: string) => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(word);
      utterance.lang = 'en-US';
      utterance.rate = 0.8;
      utterance.onstart = () => setIsPlaying(true);
      utterance.onend = () => setIsPlaying(false);
      utterance.onerror = () => {
        setIsPlaying(false);
        toast.error(isArabic ? 'خطأ في التشغيل' : 'Playback error', isArabic ? 'حدث خطأ أثناء تشغيل الصوت.' : 'An error occurred during audio playback.');
      };
      window.speechSynthesis.speak(utterance);
    } else {
      toast.error(isArabic ? 'غير مدعوم' : 'Not supported', isArabic ? 'متصفحك لا يدعم تحويل النص إلى كلام.' : 'Your browser does not support text-to-speech.');
    }
  };
  const toggleBookmark = (soundSymbol: string) => {
    setBookmarkedSounds(prev => {
      const newBookmarks = new Set(prev);
      if (newBookmarks.has(soundSymbol)) {
        newBookmarks.delete(soundSymbol);
        toast.info(isArabic ? 'تمت إزالة الإشارة المرجعية' : 'Bookmark removed', isArabic ? 'تمت إزالة الصوت من المفضلة.' : 'Sound removed from bookmarks.');
      } else {
        newBookmarks.add(soundSymbol);
        toast.success(isArabic ? 'تمت الإضافة للمفضلة' : 'Bookmark added', isArabic ? 'تمت إضافة الصوت إلى المفضلة.' : 'Sound added to bookmarks.');
      }
      return newBookmarks;
    });
  };
  const markAsCompleted = (soundSymbol: string) => {
    if (!completedSounds.has(soundSymbol)) {
      setCompletedSounds(prev => {
        const newCompleted = new Set(prev);
        newCompleted.add(soundSymbol);
        if (prev.size === 0) {
          toast.success(isArabic ? 'أحسنت!' : 'Great job!', isArabic ? 'لقد أكملت تعلم صوتك الأول.' : 'You completed learning your first sound.');
        }
        return newCompleted;
      });
    }
  };
  const startPractice = () => {
    setPracticeMode(true);
    toast.info(isArabic ? 'وضع التدريب' : 'Practice mode', isArabic ? 'استمع وكرر الكلمات لتحسين نطقك.' : 'Listen and repeat words to improve your pronunciation.');
  };
  const endPractice = () => {
    setPracticeMode(false);
  };
  const totalSounds = PRONUNCIATION_LESSONS.reduce((count, lesson) => count + lesson.sounds.length, 0);
  const progressPercentage = Math.round(completedSounds.size / totalSounds * 100);
  return <div className="w-full px-6 md:px-10 lg:px-20 py-16" data-id="element-4154">
      <Sitemap currentPage={{
      en: 'Pronunciation Guide',
      ar: 'دليل النطق'
    }} breadcrumbs={[{
      label: {
        en: 'Resources',
        ar: 'المصادر'
      },
      href: '/resources'
    }]} data-id="element-4155" />
      <div className="max-w-7xl mx-auto" data-id="element-4156">
        <div className="text-center mb-8" data-id="element-4157">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4" data-id="element-4158">
            {isArabic ? 'دليل النطق' : 'Pronunciation Guide'}
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto" data-id="element-4159">
            {isArabic ? 'تعلم نطق الأصوات الإنجليزية بشكل صحيح مع أمثلة صوتية' : 'Learn to pronounce English sounds correctly with audio examples'}
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 mb-8" data-id="element-4160">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-4161">
            <div data-id="element-4162">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-1" data-id="element-4163">
                {isArabic ? 'تقدمك في النطق' : 'Your pronunciation progress'}
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-300" data-id="element-4164">
                {isArabic ? `أكملت ${completedSounds.size} من أصل ${totalSounds} صوت` : `Completed ${completedSounds.size} out of ${totalSounds} sounds`}
              </p>
            </div>
            <div className="w-full sm:w-48" data-id="element-4165">
              <div className="flex items-center justify-between text-sm mb-1" data-id="element-4166">
                <span className="text-gray-600 dark:text-gray-400" data-id="element-4167">
                  {progressPercentage}%
                </span>
                <span className="text-gray-600 dark:text-gray-400" data-id="element-4168">
                  {isArabic ? 'مكتمل' : 'Complete'}
                </span>
              </div>
              <div className="h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden" data-id="element-4169">
                <div className="h-full bg-blue-600 dark:bg-blue-400 transition-all duration-300" style={{
                width: `${progressPercentage}%`
              }} data-id="element-4170" />
              </div>
            </div>
          </div>
        </div>
        <div className="flex justify-center mb-8" data-id="element-4171">
          <button onClick={practiceMode ? endPractice : startPractice} className={`flex items-center gap-2 px-5 py-2.5 rounded-lg transition-all transform hover:-translate-y-0.5 ${practiceMode ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'}`} data-id="element-4172">
            {practiceMode ? <>
                <CheckIcon className="w-5 h-5" data-id="element-4173" />
                <span data-id="element-4174">{isArabic ? 'إنهاء التدريب' : 'End Practice'}</span>
              </> : <>
                <MicIcon className="w-5 h-5" data-id="element-4175" />
                <span data-id="element-4176">{isArabic ? 'بدء التدريب' : 'Start Practice'}</span>
              </>}
          </button>
        </div>
        <div className="bg-blue-50 dark:bg-blue-900/30 rounded-xl p-6 mb-12" data-id="element-4177">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2" data-id="element-4178">
            <InfoIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" data-id="element-4179" />
            {isArabic ? 'نصائح للتمرين' : 'Practice Tips'}
          </h2>
          <ul className="space-y-3" data-id="element-4180">
            <li className="flex items-center gap-3" data-id="element-4181">
              <RepeatIcon className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0" data-id="element-4182" />
              <span className="text-gray-700 dark:text-gray-300" data-id="element-4183">
                {isArabic ? 'كرر الكلمات بصوت عالٍ بعد الاستماع إليها' : 'Repeat words aloud after listening to them'}
              </span>
            </li>
            <li className="flex items-center gap-3" data-id="element-4184">
              <MicIcon className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0" data-id="element-4185" />
              <span className="text-gray-700 dark:text-gray-300" data-id="element-4186">
                {isArabic ? 'سجل نفسك وقارن نطقك مع النطق الصحيح' : 'Record yourself and compare with the correct pronunciation'}
              </span>
            </li>
            {practiceMode && <li className="flex items-start gap-3 bg-green-50 dark:bg-green-900/20 p-3 rounded-lg border border-green-100 dark:border-green-800 mt-4" data-id="element-4187">
                <CheckCircleIcon className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" data-id="element-4188" />
                <span className="text-gray-800 dark:text-gray-200" data-id="element-4189">
                  {isArabic ? 'أنت الآن في وضع التدريب! انقر على أي كلمة للاستماع، ثم كرر النطق بصوت عالٍ. انقر على زر "تم" عندما تشعر أنك أتقنت الصوت.' : 'You are now in practice mode! Click any word to listen, then repeat the pronunciation aloud. Click the "Done" button when you feel you have mastered the sound.'}
                </span>
              </li>}
          </ul>
        </div>
        <div className="space-y-12" data-id="element-4190">
          {PRONUNCIATION_LESSONS.map(lesson => <div key={lesson.id} data-id="element-4191">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6" data-id="element-4192">
                {isArabic ? lesson.category.ar : lesson.category.en}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4193">
                {lesson.sounds.map(sound => <div key={sound.symbol} className={`bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 transition-all ${selectedSound === sound.symbol ? 'ring-2 ring-blue-500 dark:ring-blue-400' : ''} ${completedSounds.has(sound.symbol) ? 'border-l-4 border-green-500 dark:border-green-400' : ''}`} data-id="element-4194">
                    <div className="flex items-center justify-between mb-4" data-id="element-4195">
                      <div className="flex items-center gap-3" data-id="element-4196">
                        <div className="text-2xl font-bold text-blue-600 dark:text-blue-400" data-id="element-4197">
                          {sound.symbol}
                        </div>
                        {completedSounds.has(sound.symbol) && <Tooltip content={isArabic ? 'تم التعلم' : 'Learned'} data-id="element-4198">
                            <span className="p-1 bg-green-100 dark:bg-green-900/30 rounded-full" data-id="element-4199">
                              <CheckCircleIcon className="w-4 h-4 text-green-600 dark:text-green-400" data-id="element-4200" />
                            </span>
                          </Tooltip>}
                      </div>
                      <Tooltip content={bookmarkedSounds.has(sound.symbol) ? isArabic ? 'إزالة من المفضلة' : 'Remove bookmark' : isArabic ? 'إضافة للمفضلة' : 'Add bookmark'} data-id="element-4201">
                        <button onClick={() => toggleBookmark(sound.symbol)} className={`p-2 rounded-full transition-colors ${bookmarkedSounds.has(sound.symbol) ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`} aria-label={bookmarkedSounds.has(sound.symbol) ? 'Remove bookmark' : 'Add bookmark'} data-id="element-4202">
                          <BookmarkIcon className="w-5 h-5" data-id="element-4203" />
                        </button>
                      </Tooltip>
                    </div>
                    <div className="space-y-4" data-id="element-4204">
                      {sound.examples.map((example, index) => <div key={index} className={`flex items-center justify-between p-3 rounded-lg transition-all ${practiceMode ? 'bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 cursor-pointer' : 'bg-gray-50 dark:bg-gray-900/50 hover:bg-gray-100 dark:hover:bg-gray-900/70'}`} onClick={() => playSound(example.word)} data-id="element-4205">
                          <div data-id="element-4206">
                            <div className="font-medium text-gray-900 dark:text-white mb-1 flex items-center gap-2" data-id="element-4207">
                              {example.word}
                              <span className="text-sm text-gray-500 dark:text-gray-400" data-id="element-4208">
                                {example.phonetic}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-4" data-id="element-4209">
                            <span className="text-blue-600 dark:text-blue-400" dir="rtl" data-id="element-4210">
                              {example.ar}
                            </span>
                            <Tooltip content={isArabic ? 'استمع إلى النطق' : 'Listen to pronunciation'} data-id="element-4211">
                              <button className={`p-2 rounded-full transition-colors ${isPlaying ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 animate-pulse' : 'text-gray-500 hover:text-blue-600 dark:hover:text-blue-400'}`} aria-label="Play sound" onClick={e => {
                        e.stopPropagation();
                        playSound(example.word);
                      }} data-id="element-4212">
                                <VolumeIcon className="w-5 h-5" data-id="element-4213" />
                              </button>
                            </Tooltip>
                          </div>
                        </div>)}
                    </div>
                    {practiceMode && <div className="mt-4 flex justify-end" data-id="element-4214">
                        <button onClick={() => markAsCompleted(sound.symbol)} className={`px-4 py-2 rounded-lg transition-colors ${completedSounds.has(sound.symbol) ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400' : 'bg-blue-600 hover:bg-blue-700 text-white'}`} data-id="element-4215">
                          {completedSounds.has(sound.symbol) ? isArabic ? 'تم التعلم' : 'Learned' : isArabic ? 'تم التدرب' : 'Mark as practiced'}
                        </button>
                      </div>}
                  </div>)}
              </div>
            </div>)}
        </div>
        <div className="mt-12 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl p-8" data-id="element-4216">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4" data-id="element-4217">
            {isArabic ? 'الخطوات التالية' : 'Next Steps'}
          </h2>
          <p className="text-gray-700 dark:text-gray-300 mb-6" data-id="element-4218">
            {isArabic ? 'بعد تعلم النطق الصحيح، استمر في تطوير مهاراتك اللغوية مع هذه الموارد' : 'After learning proper pronunciation, continue developing your language skills with these resources'}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-4219">
            <Link to="/resources/grammar" className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 rounded-lg shadow-sm hover:shadow transition-all group" data-id="element-4220">
              <div className="flex items-center gap-3" data-id="element-4221">
                <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg" data-id="element-4222">
                  <BookIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" data-id="element-4223" />
                </div>
                <span className="font-medium text-gray-900 dark:text-white" data-id="element-4224">
                  {isArabic ? 'دليل القواعد' : 'Grammar Guide'}
                </span>
              </div>
              <ArrowRightIcon className="w-5 h-5 text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 transform group-hover:translate-x-1 transition-all" data-id="element-4225" />
            </Link>
            <Link to="/resources/vocabulary" className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 rounded-lg shadow-sm hover:shadow transition-all group" data-id="element-4226">
              <div className="flex items-center gap-3" data-id="element-4227">
                <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg" data-id="element-4228">
                  <BookIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" data-id="element-4229" />
                </div>
                <span className="font-medium text-gray-900 dark:text-white" data-id="element-4230">
                  {isArabic ? 'قوائم المفردات' : 'Vocabulary Lists'}
                </span>
              </div>
              <ArrowRightIcon className="w-5 h-5 text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 transform group-hover:translate-x-1 transition-all" data-id="element-4231" />
            </Link>
          </div>
        </div>
      </div>
    </div>;
}